import { Injectable } from '@angular/core';
import { ResearchAccess } from '../models/research.models';
import { PortalConfigService } from './portal-config.service';

/** Membership states a token can express — the same three the server's `MembershipFromClaims` returns. */
export const MEMBERSHIP_STATUSES = ['active', 'lapsed', 'unknown'] as const;
export type MembershipStatus = (typeof MEMBERSHIP_STATUSES)[number];

const MONTHS: readonly string[] = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** Narrows the wire string; anything unrecognised is `unknown`, which renders nothing. */
export function ToMembershipStatus(value: string | null | undefined): MembershipStatus {
    return MEMBERSHIP_STATUSES.find((s) => s === value) ?? 'unknown';
}

/** "30 Sep 2026" — the Since card's date form (P/proto-states.js:134), from an ISO date; null when absent or unparseable. */
export function FormatMembershipDate(iso: string | null): string | null {
    if (!iso) return null;
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return null;
    return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}`;
}

/** The lapsed strip's sentence (P/proto-states.js:134); the "on {date}" clause drops when the token carried no date. */
export function LapsedBannerText(endsAtIso: string | null): string {
    const date = FormatMembershipDate(endsAtIso);
    return `Your membership ended${date ? ` on ${date}` : ''}. Renew to keep your matches, saved items and Career Explorer.`;
}

/** The Home CV module shows only for a member with no CV, outside the quiet first visit, and not while lapsed (P/proto-states.js:139). */
export function ShowCvModule(hasCV: boolean, homeQuiet: boolean, lapsed: boolean): boolean {
    return !hasCV && !homeQuiet && !lapsed;
}

/**
 * The member's membership state for this surface session, set by `RaSurfaceComponent` from every
 * `researchAccess` read. This is the seam modules read: People Finder's lock, Home's CV module, and
 * Career Explorer's lock (Task 28) all ask `IsLapsed`. Provided per surface like `PortalConfigService`.
 * `unknown` — what the token yields today — is not lapsed, so nothing changes until the claims exist.
 */
@Injectable()
export class MembershipService {
    Status: MembershipStatus = 'unknown';
    EndsAt: string | null = null;

    get IsLapsed(): boolean {
        return this.Status === 'lapsed';
    }

    /** Opens the AGU account page (`researchPortalConfig.accountSettingsUrl`) in a new tab; a failed config read does nothing. */
    async OpenRenewal(portalConfig: PortalConfigService | null): Promise<void> {
        try {
            const config = await portalConfig?.Load();
            if (config?.accountSettingsUrl) window.open(config.accountSettingsUrl, '_blank', 'noopener');
        } catch {
            // Non-fatal: the page stays as it is; the member can use Settings' account link.
        }
    }

    Set(access: Pick<ResearchAccess, 'membershipStatus' | 'membershipEndsAt'>): void {
        this.Status = ToMembershipStatus(access.membershipStatus);
        this.EndsAt = access.membershipEndsAt ?? null;
    }
}
